import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Container, Chip, Divider, Paper, AppBar,
  Toolbar, Button, Drawer, List, ListItem, ListItemButton, ListItemText,
  useTheme, useMediaQuery, Card, CardContent, Tabs, Tab, IconButton,
  Collapse
} from '@mui/material';
import { Code, ArrowBack, Menu as MenuIcon, ContentCopy, CheckCircle, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import { agentChatAPIs, openaiSDKCompatibility, systemOverview } from './DocumentationData';

const Documentation = () => {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    'agent-chat': true,
    'openai-sdk': true
  });
  const [codeTabs, setCodeTabs] = useState({});
  const [copiedCode, setCopiedCode] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[id^="api-"], [id^="openai-sdk-"]');
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'success';
      case 'POST': return 'primary';
      case 'PUT': return 'warning';
      case 'PATCH': return 'info';
      case 'DELETE': return 'error';
      default: return 'default';
    }
  };

  const generateId = (title) => {
    return `api-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
  };

  const generateCodeExample = (api, format) => {
    const baseUrl = api.endpoint.startsWith('/') 
      ? `https://YOUR_AGENT_ENDPOINT${api.endpoint}`
      : api.endpoint;

    const headers = api.headers || [];
    const apiKey = headers.find(h => h.name === 'x-api-key') 
      ? 'YOUR_API_KEY' 
      : null;
    const authHeader = headers.find(h => h.name === 'Authorization')
      ? 'Bearer YOUR_TOKEN'
      : null;

    const getBodyData = () => {
      if (!api.body) return null;
      const body = {};
      Object.entries(api.body).forEach(([key, param]) => {
        if (typeof param === 'object' && param.example !== undefined) {
          body[key] = param.example;
        } else if (typeof param === 'object' && param.default) {
          body[key] = param.default.replace(/"/g, '');
        }
      });
      return body;
    };

    const bodyData = getBodyData();
    const hasFormData = api.usesFormData;

    switch (format) {
      case 'curl':
        let curlCmd = `curl -X ${api.method} "${baseUrl}"`;
        
        headers.forEach(header => {
          if (header.name === 'x-api-key') {
            curlCmd += ` \\\n  -H "x-api-key: ${apiKey}"`;
          } else if (header.name === 'Authorization') {
            curlCmd += ` \\\n  -H "Authorization: ${authHeader}"`;
          } else if (header.name === 'Content-Type' && !hasFormData) {
            curlCmd += ` \\\n  -H "Content-Type: application/json"`;
          }
        });

        if (api.method !== 'GET' && bodyData) {
          if (hasFormData) {
            Object.entries(bodyData).forEach(([key, value]) => {
              if (key === 'audioFile' || key === 'files') {
                curlCmd += ` \\\n  -F "${key}=@/path/to/file"`;
              } else {
                curlCmd += ` \\\n  -F "${key}=${value}"`;
              }
            });
          } else {
            curlCmd += ` \\\n  -d '${JSON.stringify(bodyData, null, 2)}'`;
          }
        } else if (api.queryParams) {
          const queryString = Object.entries(api.queryParams)
            .map(([key, param]) => {
              const example = typeof param === 'object' && param.example !== undefined 
                ? param.example 
                : (typeof param === 'object' && param.default ? param.default.replace(/"/g, '') : 'value');
              return `${key}=${example}`;
            })
            .join('&');
          if (queryString) {
            curlCmd = curlCmd.replace(`"${baseUrl}"`, `"${baseUrl}?${queryString}"`);
          }
        }

        return curlCmd;

      case 'javascript':
        let jsCode = '';
        
        if (hasFormData) {
          jsCode = `const formData = new FormData();\n`;
          Object.entries(bodyData || {}).forEach(([key, value]) => {
            if (key === 'audioFile' || key === 'files') {
              jsCode += `formData.append('${key}', fileInput.files[0]);\n`;
            } else {
              jsCode += `formData.append('${key}', ${typeof value === 'string' ? `'${value}'` : value});\n`;
            }
          });
          jsCode += `\nconst response = await fetch('${baseUrl}', {\n`;
          jsCode += `  method: '${api.method}',\n`;
          headers.forEach(header => {
            if (header.name === 'x-api-key') {
              jsCode += `  headers: {\n    'x-api-key': '${apiKey}'\n  },\n`;
            } else if (header.name === 'Authorization') {
              jsCode += `  headers: {\n    'Authorization': '${authHeader}'\n  },\n`;
            }
          });
          jsCode += `  body: formData\n`;
          jsCode += `});\n\nconst data = await response.json();`;
        } else {
          jsCode = `const response = await fetch('${baseUrl}', {\n`;
          jsCode += `  method: '${api.method}',\n`;
          
          const headerObj = {};
          headers.forEach(header => {
            if (header.name === 'x-api-key') {
              headerObj['x-api-key'] = apiKey;
            } else if (header.name === 'Authorization') {
              headerObj['Authorization'] = authHeader;
            } else if (header.name === 'Content-Type' && api.method !== 'GET') {
              headerObj['Content-Type'] = 'application/json';
            }
          });
          
          if (Object.keys(headerObj).length > 0) {
            jsCode += `  headers: ${JSON.stringify(headerObj, null, 2).replace(/\n/g, '\n  ')}\n`;
          }
          
          if (api.method !== 'GET' && bodyData) {
            jsCode += `  body: JSON.stringify(${JSON.stringify(bodyData, null, 2).replace(/\n/g, '\n    ')})\n`;
          }
          
          jsCode += `});\n\nconst data = await response.json();`;
        }
        
        return jsCode;

      case 'python':
        let pyCode = `import requests\n`;
        
        if (hasFormData) {
          pyCode += `\nfiles = {`;
          const formFields = [];
          Object.entries(bodyData || {}).forEach(([key, value]) => {
            if (key === 'audioFile' || key === 'files') {
              formFields.push(`    '${key}': open('path/to/file', 'rb')`);
            } else {
              formFields.push(`    '${key}': ${typeof value === 'string' ? `'${value}'` : value}`);
            }
          });
          pyCode += formFields.join(',\n');
          pyCode += `\n}\n\n`;
          
          pyCode += `headers = {`;
          const headerFields = [];
          headers.forEach(header => {
            if (header.name === 'x-api-key') {
              headerFields.push(`    'x-api-key': '${apiKey}'`);
            } else if (header.name === 'Authorization') {
              headerFields.push(`    'Authorization': '${authHeader}'`);
            }
          });
          pyCode += headerFields.join(',\n');
          pyCode += `\n}\n\n`;
          
          pyCode += `response = requests.${api.method.toLowerCase()}('${baseUrl}', files=files, headers=headers)\ndata = response.json()`;
        } else {
          pyCode += `\nheaders = {`;
          const headerFields = [];
          headers.forEach(header => {
            if (header.name === 'x-api-key') {
              headerFields.push(`    'x-api-key': '${apiKey}'`);
            } else if (header.name === 'Authorization') {
              headerFields.push(`    'Authorization': '${authHeader}'`);
            } else if (header.name === 'Content-Type' && api.method !== 'GET') {
              headerFields.push(`    'Content-Type': 'application/json'`);
            }
          });
          pyCode += headerFields.join(',\n');
          pyCode += `\n}\n\n`;
          
          if (api.method !== 'GET' && bodyData) {
            pyCode += `data = ${JSON.stringify(bodyData, null, 2).replace(/\n/g, '\n')}\n\n`;
            pyCode += `response = requests.${api.method.toLowerCase()}('${baseUrl}', json=data, headers=headers)\n`;
          } else {
            pyCode += `response = requests.${api.method.toLowerCase()}('${baseUrl}', headers=headers)\n`;
          }
          pyCode += `data = response.json()`;
        }
        
        return pyCode;

      default:
        return '';
    }
  };

  const handleCopyCode = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleTabChange = (apiId, format) => {
    setCodeTabs(prev => ({
      ...prev,
      [apiId]: format
    }));
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const renderCodeExample = (api) => {
    const apiId = generateId(api.title);
    const currentTab = codeTabs[apiId] || 'curl';
    const code = generateCodeExample(api, currentTab);

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
          Code Examples
        </Typography>
        <Paper sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => handleTabChange(apiId, newValue)}
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.default',
              minHeight: 40
            }}
          >
            <Tab label="cURL" value="curl" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
            <Tab label="JavaScript" value="javascript" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
            <Tab label="Python" value="python" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
          </Tabs>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleCopyCode(code, apiId)}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                {copiedCode === apiId ? (
                  <CheckCircle fontSize="small" color="success" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>
            </Box>
            <Box
              component="pre"
              sx={{
                p: 3,
                m: 0,
                bgcolor: 'background.default',
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: 'text.primary',
                maxHeight: '500px'
              }}
            >
              <code>{code}</code>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderParameter = (key, param, index) => {
    return (
      <Box key={index} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.95rem' }}>
            {key}
          </Typography>
          {typeof param === 'object' && param.required && (
            <Chip label="required" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
          {typeof param === 'object' && param.default && (
            <Chip label={`default: ${param.default}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} color="default" />
          )}
        </Box>
        {typeof param === 'object' ? (
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Type:</strong> <code style={{ fontSize: '0.85rem' }}>{param.type}</code>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {param.description}
            </Typography>
            {param.options && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Options:</strong> {Array.isArray(param.options) ? param.options.join(', ') : param.options}
              </Typography>
            )}
            {param.range && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Range:</strong> {param.range}
              </Typography>
            )}
            {param.minimum !== undefined && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Minimum:</strong> {param.minimum}
              </Typography>
            )}
            {param.maximum !== undefined && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Maximum:</strong> {param.maximum}
              </Typography>
            )}
            {param.maxLength && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Max Length:</strong> {param.maxLength}
              </Typography>
            )}
            {param.minItems && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Min Items:</strong> {param.minItems}
              </Typography>
            )}
            {param.acceptedFormats && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Accepted Formats:</strong> {Array.isArray(param.acceptedFormats) ? param.acceptedFormats.join(', ') : param.acceptedFormats}
              </Typography>
            )}
            {param.maxSize && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Max Size:</strong> {param.maxSize}
              </Typography>
            )}
            {param.format && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                <strong>Format:</strong> {param.format}
              </Typography>
            )}
            {param.example && (
              <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  <strong>Example:</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {typeof param.example === 'object' ? JSON.stringify(param.example, null, 2) : param.example}
                </Typography>
              </Box>
            )}
            {param.note && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem', display: 'block', mt: 0.5 }}>
                {param.note}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
            {param}
          </Typography>
        )}
      </Box>
    );
  };

  const renderAPIEndpoint = (api, index) => {
    const id = generateId(api.title);
    
    return (
      <Card
        id={id}
        key={index}
        sx={{
          mb: 4,
          scrollMarginTop: '80px',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, pb: 2, borderBottom: `2px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={api.method} color={getMethodColor(api.method)} size="small" />
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  flex: 1
                }}
              >
                {api.title}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.95rem',
                color: 'text.secondary',
                bgcolor: 'background.default',
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                display: 'inline-block'
              }}
            >
              {api.endpoint}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '1rem' }}>
              {api.description}
            </Typography>
          </Box>

          {api.note && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Note:</strong> {api.note}</Typography>
            </Paper>
          )}

          {renderCodeExample(api)}

          {api.headers && api.headers.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>Headers</Typography>
              <Box sx={{ pl: 2 }}>
                {api.headers.map((header, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.95rem' }}>
                        {header.name}
                      </Typography>
                      {header.required && <Chip label="required" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                      {header.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {api.queryParams && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>Query Parameters</Typography>
              <Box sx={{ pl: 2 }}>
                {Object.entries(api.queryParams).map(([key, param], idx) => renderParameter(key, param, idx))}
              </Box>
            </Box>
          )}

          {api.body && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>Request Body</Typography>
              {api.usesFormData ? (
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>FormData with the following fields:</Typography>
                  {Object.entries(api.body).map(([key, param], idx) => renderParameter(key, param, idx))}
                </Box>
              ) : (
                <Box sx={{ pl: 2 }}>
                  {Object.entries(api.body).map(([key, param], idx) => renderParameter(key, param, idx))}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1.1rem' }}>Response</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
              {api.response}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: 600 }}>
        Documentation
      </Typography>
      <List sx={{ px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => scrollToSection('overview')}
            selected={activeSection === 'overview'}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }
            }}
          >
            <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>Overview</Typography>} />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => toggleSection('agent-chat')}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 1,
              mb: 0.5,
              mt: 1
            }}
          >
            <ListItemText 
              primary={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Agent Chat</Typography>} 
            />
            {expandedSections['agent-chat'] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={expandedSections['agent-chat']} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {agentChatAPIs.map((api, idx) => {
              const id = generateId(api.title);
              return (
                <ListItem key={idx} disablePadding>
                  <ListItemButton
                    onClick={() => scrollToSection(id)}
                    selected={activeSection === id}
                    sx={{
                      py: 0.75,
                      px: 2,
                      borderRadius: 1,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        }
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <Chip
                              label={api.method}
                              color={getMethodColor(api.method)}
                              size="small"
                              sx={{ height: 16, fontSize: '0.65rem', minWidth: 35 }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              {api.title.length > 40 ? api.title.substring(0, 40) + '...' : api.title}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              display: 'block',
                              ml: 4.5
                            }}
                          >
                            {api.endpoint}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => toggleSection('openai-sdk')}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 1,
              mb: 0.5,
              mt: 1
            }}
          >
            <ListItemText 
              primary={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>OpenAI SDK Compatibility</Typography>} 
            />
            {expandedSections['openai-sdk'] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={expandedSections['openai-sdk']} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {openaiSDKCompatibility.sections.map((section, idx) => {
              const id = `openai-sdk-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
              return (
                <ListItem key={idx} disablePadding>
                  <ListItemButton
                    onClick={() => scrollToSection(id)}
                    selected={activeSection === id}
                    sx={{
                      py: 0.75,
                      px: 2,
                      borderRadius: 1,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        }
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {section.title}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: alpha(theme.palette.background[theme.palette.mode === 'dark' ? 'default' : 'paper'], 0.9),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isMobile && (
                <Button
                  onClick={handleDrawerToggle}
                  sx={{ color: 'common.white', minWidth: 'auto', mr: 1 }}
                >
                  <MenuIcon />
                </Button>
              )}
              <img
                src="/WhiteLabelLogos/utw.png"
                alt="UT Logo"
                style={{ 
                  height: '40px',
                  filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
                }}
              />
              <Typography variant="h6" component={Link} to="/" sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 600, fontSize: '1rem', whiteSpace: 'nowrap' }}>
                UT
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBack />}
              sx={{ color: 'common.white' }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: 280 },
          flexShrink: { md: 0 },
          display: { xs: 'none', md: 'block' }
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              borderRight: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              pt: 10
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper'
          }
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - 280px)` },
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ pt: 10, pb: 8 }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
            <Box id="overview" sx={{ mb: 8, scrollMarginTop: '80px' }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                API Documentation
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                Complete reference for all PioSphere APIs to integrate AI capabilities into your applications
              </Typography>

              <Card sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    {systemOverview.title}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {systemOverview.sections.map((section, index) => (
                      <Box key={index}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
                          {section.title}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                          {section.description}
                        </Typography>
                        {section.baseUrl && (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', mt: 1 }}>
                            Base URL: <code style={{ color: 'primary.main' }}>{section.baseUrl}</code>
                          </Typography>
                        )}
                        {index < systemOverview.sections.length - 1 && <Divider sx={{ mt: 3 }} />}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box id="agent-chat-apis" sx={{ mb: 8, scrollMarginTop: '80px' }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                Agent Chat APIs
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                APIs for interacting with deployed agents, managing conversations, and handling knowledge base operations
              </Typography>
              {agentChatAPIs.map((api, index) => renderAPIEndpoint(api, index))}
            </Box>

            <Box id="openai-sdk-compatibility" sx={{ mb: 8, scrollMarginTop: '80px' }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                {openaiSDKCompatibility.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                {openaiSDKCompatibility.description}
              </Typography>

              {openaiSDKCompatibility.sections.map((section, index) => {
                const sectionId = `openai-sdk-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
                return (
                  <Card
                    id={sectionId}
                    key={index}
                    sx={{
                      mb: 4,
                      scrollMarginTop: '80px',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 3, fontSize: '1rem' }}>
                        {section.content}
                      </Typography>

                      {section.codeExamples && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                            Code Examples
                          </Typography>
                          <Paper sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
                            <Tabs
                              value={codeTabs[sectionId] || 'javascript'}
                              onChange={(e, newValue) => handleTabChange(sectionId, newValue)}
                              sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                bgcolor: 'background.default',
                                minHeight: 40
                              }}
                            >
                              <Tab label="JavaScript" value="javascript" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
                              <Tab label="Python" value="python" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
                              <Tab label="cURL" value="curl" sx={{ minHeight: 40, fontSize: '0.875rem' }} />
                            </Tabs>
                            <Box sx={{ position: 'relative' }}>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  zIndex: 1
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyCode(section.codeExamples[codeTabs[sectionId] || 'javascript'], sectionId)}
                                  sx={{
                                    bgcolor: 'background.paper',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                >
                                  {copiedCode === sectionId ? (
                                    <CheckCircle fontSize="small" color="success" />
                                  ) : (
                                    <ContentCopy fontSize="small" />
                                  )}
                                </IconButton>
                              </Box>
                              <Box
                                component="pre"
                                sx={{
                                  p: 3,
                                  m: 0,
                                  bgcolor: 'background.default',
                                  overflow: 'auto',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                  color: 'text.primary',
                                  maxHeight: '500px'
                                }}
                              >
                                <code>{section.codeExamples[codeTabs[sectionId] || 'javascript']}</code>
                              </Box>
                            </Box>
                          </Paper>
                        </Box>
                      )}

                      {section.differences && (
                        <Box sx={{ mt: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                            Key Differences
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            {section.differences.map((diff, idx) => (
                              <Box key={idx} sx={{ mb: 2 }}>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.95rem', mb: 0.5 }}>
                                  {diff.topic}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                                  {diff.description}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Documentation;
